/**
 * Whether this browser can give us a WebGL2 context (Three.js r16x+ needs WebGL2). Probed once:
 * every probe creates a context, and a page may only hold about sixteen before the browser
 * starts evicting the oldest ones, which turns the live scene black. The probe's own context is
 * released at once. A failure while the page is hidden (a tab opened in the background) is not
 * cached: the GPU process may simply not have been there yet.
 */
let cached: boolean | null = null
let renderer = ''

/** The GPU's name as the browser reports it (empty when it will not say), from the same single probe. */
export const gpuRenderer = (): string => {
  hasWebGL()
  return renderer
}

export const hasWebGL = (): boolean => {
  if (cached !== null) return cached
  let ok = false
  try {
    // Hardware first; failing that, whatever the browser will give (a software renderer, say):
    // the scene's tiers know to run it lean. A fresh canvas for the second try: a canvas
    // remembers a failed context.
    const canvas = document.createElement('canvas')
    const gl =
      canvas.getContext('webgl2', { failIfMajorPerformanceCaveat: false }) ??
      document.createElement('canvas').getContext('webgl2', { powerPreference: 'low-power' })
    ok = !!gl
    const info = gl?.getExtension('WEBGL_debug_renderer_info')
    if (gl && info) renderer = String(gl.getParameter(info.UNMASKED_RENDERER_WEBGL) ?? '')
    gl?.getExtension('WEBGL_lose_context')?.loseContext()
  } catch {
    ok = false
  }
  if (ok || document.visibilityState === 'visible') cached = ok
  return ok
}
